/**
 * Points Purchase Page
 * Allows viewers to purchase points
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PointsPurchaseTerms from '../components/PointsPurchaseTerms';
import { Link } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { usePoints } from '../hooks/usePoints';
import { ROUTES } from '../lib/constants';
import { AnalyticsLogger } from '../lib/analytics';

const POINT_OPTIONS = [
  { amount: 1000 as const, price: 1000, label: '1,000pt' },
  { amount: 3000 as const, price: 3000, label: '3,000pt' },
  { amount: 5000 as const, price: 5000, label: '5,000pt' },
  { amount: 10000 as const, price: 10000, label: '10,000pt' },
];

export default function Points() {
  const { t } = useTranslation('common');
  const { user, logout } = useAuth();
  const { points, isLoading, error, purchasePoints } = usePoints();
  const [selectedAmount, setSelectedAmount] = useState<1000 | 3000 | 5000 | 10000 | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);

  const handleLogout = () => {
    logout();
    // Points page is viewer-only, always redirect to viewer login
    window.location.href = ROUTES.LOGIN;
  };

  const handlePurchase = async () => {
    if (!selectedAmount) return;

    try {
      setPurchasing(true);
      setPurchaseSuccess(false);
      await purchasePoints(selectedAmount);
      // 分析イベント: ポイント購入成功
      AnalyticsLogger.getInstance().trackPurchase(selectedAmount, selectedAmount);
      setPurchaseSuccess(true);
      setSelectedAmount(null);
      setTimeout(() => setPurchaseSuccess(false), 3000);
    } catch {
      // Error is handled by usePoints hook
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Bar */}
      <header className="bg-accent text-white shadow-md">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold flex items-center">
              <span className="mr-2">💰</span>
              {t('points.purchase.title')}
            </h1>
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="opacity-90">{t('header.loggedInAs')}:</span> <span className="font-semibold">{user?.username}</span>
              </div>
              <div className="flex gap-2">
                <Link
                  to={ROUTES.STUDIO}
                  className="bg-accent-50 text-accent-700 hover:bg-accent-100 text-sm font-semibold py-2 px-4 rounded transition-colors shadow-sm"
                >
                  {t('header.studio')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 px-4 rounded transition-colors"
                >
                  {t('buttons.logout')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Subtitle */}
        <div className="text-center mb-8">
          <p className="text-lg text-gray-600">
            {t('points.purchase.subtitle')}
          </p>
        </div>

        {/* Current Balance */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="bg-gradient-to-br from-accent-50 to-accent-100 rounded-lg p-6 shadow-lg border border-accent-200">
            <div className="text-center">
              <div className="text-gray-700 text-sm mb-2 font-medium">{t('points.purchase.currentBalance')}</div>
              <div className="text-5xl font-bold text-accent-600">
                {isLoading ? (
                  <span className="animate-pulse">...</span>
                ) : (
                  <span>{points?.toLocaleString() || 0} pt</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Purchase Options */}
        <div className="max-w-4xl mx-auto mb-8">
          <h2 className="text-xl font-bold mb-6 text-center text-gray-800">
            {t('points.purchase.selectAmount')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {POINT_OPTIONS.map((option) => (
              <button
                key={option.amount}
                onClick={() => setSelectedAmount(option.amount)}
                className={`p-5 rounded-lg border-2 transition-all ${
                  selectedAmount === option.amount
                    ? 'border-accent bg-accent-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-accent-300 hover:shadow'
                }`}
              >
                <div className="text-3xl font-bold text-accent mb-2">
                  {option.label}
                </div>
                <div className="text-gray-600 text-sm font-medium">{t('points.taxIncludedPrice', { amount: option.price.toLocaleString('en-US') })}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Purchase Button */}
        <div className="max-w-md mx-auto mb-8">
          <button
            onClick={handlePurchase}
            disabled={!selectedAmount || purchasing}
            className={`w-full py-4 px-6 rounded-lg font-bold text-lg transition-all ${
              !selectedAmount || purchasing
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-accent hover:bg-accent-600 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {purchasing ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t('points.purchase.processing')}
              </span>
            ) : (
              selectedAmount ? t('points.purchase.purchaseButton', { amount: selectedAmount }) : t('points.purchase.selectPoints')
            )}
          </button>
        </div>

        {/* Success Message */}
        {purchaseSuccess && (
          <div className="max-w-md mx-auto mb-8">
            <div className="bg-success-50 border border-success rounded-lg p-4 text-center">
              <div className="text-success-700 font-semibold">
                ✅ {t('points.purchase.success')}
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="max-w-md mx-auto mb-8">
            <div className="bg-red-50 border border-red-500 rounded-lg p-4 text-center">
              <div className="text-red-600 font-semibold">⚠️ {error}</div>
            </div>
          </div>
        )}

        {/* Usage Guide */}
        <div className="max-w-2xl mx-auto bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-bold mb-4 text-gray-800 flex items-center">
            <span className="text-2xl mr-2">💡</span>
            {t('points.guide.title')}
          </h3>
          <ul className="space-y-3 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="text-success mr-2">•</span>
              <span><strong className="text-success-600">{t('points.guide.chatRoomLabel')}</strong> {t('points.guide.chatRoomCost')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-success mr-2">•</span>
              <span><strong className="text-success-600">{t('points.guide.peekRoomLabel')}</strong> {t('points.guide.peekRoomCost')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary mr-2">•</span>
              <span><strong className="text-primary-600">{t('points.guide.secretMessageLabel')}</strong> {t('points.guide.secretMessageCost')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-gray-400 mr-2">•</span>
              <span>{t('points.guide.free')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-gray-400 mr-2">•</span>
              <span>{t('points.guide.insufficient')}</span>
            </li>
            <li className="flex items-start">
              <span className="text-gray-400 mr-2">•</span>
              <span>{t('points.guide.balance')}</span>
            </li>
          </ul>
          <div className="mt-5 border-t border-gray-200 pt-5 text-sm">
            <PointsPurchaseTerms legalLink={
              <Link to={ROUTES.LAW} className="mt-2 inline-block text-pink-700 underline">{t('points.terms.legalLink')}</Link>
            } />
          </div>
        </div>
      </div>
    </div>
  );
}
